// src/components/Header/Header.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  HelpCircle,
  Send,
  Users,
  RefreshCw,
  Gauge
} from 'lucide-react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { PlanModal } from './PlanModal';
import { ShareModal } from './ShareModal';
import { HelpModal } from './HelpModal';
import { SearchModal } from './SearchModal';
import { QuickLinksMenu } from './QuickLinksMenu';
import { ProfileDropdown } from './ProfileDropdown';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { NotificationBell } from '../Notifications/NotificationBell';
import Logo from '../../assets/header-logo.svg';
import './Header.scss';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const [showMobileSearch] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  // Modal states
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isQuickLinksOpen, setIsQuickLinksOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  // Refs for positioning dropdowns
  const quickLinksButtonRef = useRef<HTMLButtonElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  // Keyboard shortcut Ctrl+K to open search modal
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handlers
  const handleBrandClick = () => {
    navigate('/dashboard');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleTeamClick = () => {
    navigate('/team');
  };

  const handleSearchClick = () => {
    setIsSearchModalOpen(true);
  };

  // Get user initials (first 2 characters of name)
  const getUserInitials = () => {
    if (!user?.name) return 'US';
    const names = user.name.trim().split(' ');
    if (names.length >= 2) {
      // First letter of first name + first letter of last name
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    // Just take first 2 characters of single name
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          {/* Left Section - Brand */}
          <div className="header-left">
            <div
              className="header-brand"
              onClick={handleBrandClick}
              style={{ cursor: 'pointer' }}
            >
              <img src={Logo} alt="Agile Edge Logo" className="header-logo-img" style={{ height: '32px', width: 'auto' }} />
              <span className="header-brand-name">agile edge</span>
            </div>

            {/* Current Workspace Indicator / Switcher */}
            <WorkspaceSwitcher />

            {/* See Plan Button */}
            <button
              className="see-plan-button"
              onClick={() => setIsPlanModalOpen(true)}
            >
              <Gauge className="icon" />
              <span>See Plan</span>
            </button>
          </div>

          {/* Center Section - Search (Clickable Placeholder) */}
          <div className={`header-search ${showMobileSearch ? 'mobile-active' : ''}`}>
            <div className="search-container" onClick={handleSearchClick}>
              <Search className="search-icon" />
              <div className="search-placeholder">
                Search everything...
              </div>
            </div>
          </div>

          {/* Right Section - Actions & Icons */}
          <div className="header-right">

            {/* Refresh */}
            <button
              className="header-icon-button"
              onClick={handleRefresh}
              title="Refresh page"
            >
              <RefreshCw className="icon" />
            </button>

            {/* Share */}
            <button
              className="header-icon-button"
              onClick={() => setIsShareModalOpen(true)}
              title="Share workspace"
            >
              <Send className="icon" />
            </button>

            {/* Team Members */}
            <button
              className="header-icon-button"
              onClick={handleTeamClick}
              title="Team management"
            >
              <Users className="icon" />
            </button>

            {/* Search (mobile) */}
            <button
              className="header-icon-button md-hidden"
              onClick={handleSearchClick}
              title="Search"
            >
              <Search className="icon" />
            </button>

            {/* Help */}
            <button
              className="header-icon-button"
              onClick={() => setIsHelpModalOpen(true)}
              title="Help & Support"
            >
              <HelpCircle className="icon" />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Product Switcher (Quick Links) */}
            <button
              ref={quickLinksButtonRef}
              type="button"
              className="header-icon-button product-switcher-button"
              aria-label="Quick links menu"
              title="Quick links"
              onClick={() => setIsQuickLinksOpen(!isQuickLinksOpen)}
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                width="20"
                height="20"
                aria-hidden="true"
                className="product-switcher-icon"
              >
                <g clipPath="url(#a)">
                  <path d="M4.5 2.25a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm7.75 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm7.75 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0ZM4.5 10A2.25 2.25 0 1 1 0 10a2.25 2.25 0 0 1 4.5 0Zm7.75 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0ZM20 10a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0ZM4.5 17.75a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm7.75 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm7.75 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"></path>
                </g>
                <defs>
                  <clipPath id="a">
                    <path d="M0 0h20v20H0z"></path>
                  </clipPath>
                </defs>
              </svg>
            </button>

            {/* User Avatar */}
            <button
              ref={profileButtonRef}
              className="header-avatar-button"
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              title="Profile menu"
            >
              <div className="user-avatar">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name || 'Profile'} />
                ) : (
                  <span className="user-avatar-initials">{getUserInitials()}</span>
                )}
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Modals and Dropdowns */}
      <PlanModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
      />

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />

      <QuickLinksMenu
        isOpen={isQuickLinksOpen}
        onClose={() => setIsQuickLinksOpen(false)}
        anchorEl={quickLinksButtonRef.current}
      />

      <ProfileDropdown
        isOpen={isProfileDropdownOpen}
        onClose={() => setIsProfileDropdownOpen(false)}
        anchorEl={profileButtonRef.current}
      />
    </>
  );
};